/**
 * What is actually free in a room, day by day — the storefront's calendar.
 *
 * ── WHAT THIS REPLACED ───────────────────────────────────────────────────
 * `/pl/studio` drew its picker from `OFFICE_WEEK`, a weekly rule compiled into
 * the client bundle, and knew nothing about either half of the truth: it never
 * read the calendar the panel publishes (`offering_availability`, which spec
 * §10 · I1 makes the ONLY source of bookable hours — *„klient rezerwuje tylko
 * z wpisanych godzin"*), and it never read what was already booked. So the page
 * offered hours the office had not opened and hours a customer already held,
 * and the refusal came one screen later, at checkout.
 *
 * Owner, 14.10: *„co zarezerwowane wypada; jeśli będzie obłożenie studia na
 * tydzień, to pokazuje dopiero za tydzień"*.
 *
 * ── WHY INTERVALS AND NOT SLOTS ──────────────────────────────────────────
 * {@link meetingDays} returns the start TIMES of one fixed-length meeting,
 * which is right when the length is fixed. A room is sold in six lengths and
 * the reader switches between them in the browser, so start times would have to
 * be computed six times and shipped six times. A free INTERVAL is the same
 * information at a fraction of the size, and the one arithmetic left on the
 * client — "does an N-hour block fit in it?" — is a subtraction.
 *
 * ── THE BLOCKED WINDOW, NOT THE PAID ONE ─────────────────────────────────
 * Holds arrive as `blockedFrom`/`blockedTo`, which already carry spec §10's
 * unpaid hour either side of a studio booking. That hour is time the room is
 * genuinely unavailable — resetting the set — so it disappears from the picker
 * exactly like the paid hours do. Nothing here adds a buffer; the buffer is
 * already in the pair.
 *
 * Pure: rows in, intervals out. No database and no clock beyond the `now` it is
 * handed, which is what lets it be tested without either.
 */
import { zonedDay, zonedInstant, zonedMinutes } from './period.ts'

/** Minutes past Warsaw midnight, half-open: `[from, to)`. */
export interface FreeInterval {
  readonly from: number
  readonly to: number
}

/** One day of the published calendar, with what is left of it. */
export interface StudioDay {
  readonly date: string
  readonly free: readonly FreeInterval[]
}

/** A published opening. The shape `listOpenWindows` returns. */
export interface StudioWindow {
  readonly startsAt: Date
  readonly endsAt: Date
  readonly isBookable: boolean
}

/** A reservation, reduced to the window it takes out of the calendar. */
export interface StudioHold {
  readonly blockedFrom: Date
  readonly blockedTo: Date
}

/**
 * How far ahead a room may be booked from now.
 *
 * Deliberately WIDER than anything the office has published, so that this
 * number never becomes a second answer to „how far ahead can I book?". The
 * published calendar is the only source (spec §10 · I1) and this is a bound on
 * the query, not a rule: set shorter, it would quietly close days the panel had
 * opened, and nobody editing the calendar would know why they stopped showing.
 */
export const STUDIO_HORIZON_DAYS = 365

/**
 * How soon a room may be taken.
 *
 * The same 24 hours a meeting needs, for a blunter reason: somebody has to set
 * the room up, and nobody reads the calendar at two in the morning to do it.
 */
export const STUDIO_LEAD_HOURS = 24

/** Subtract `cut` from `span`, leaving nothing, one piece or two. */
function without(span: FreeInterval, cut: FreeInterval): FreeInterval[] {
  if (cut.to <= span.from || cut.from >= span.to) return [span]
  const left = { from: span.from, to: Math.min(span.to, cut.from) }
  const right = { from: Math.max(span.from, cut.to), to: span.to }
  return [left, right].filter((piece) => piece.to > piece.from)
}

export function studioDays(input: {
  readonly windows: readonly StudioWindow[]
  readonly holds: readonly StudioHold[]
  readonly now: Date
  readonly leadHours?: number
  readonly horizonDays?: number
}): StudioDay[] {
  const leadHours = input.leadHours ?? STUDIO_LEAD_HOURS
  const horizonDays = input.horizonDays ?? STUDIO_HORIZON_DAYS
  const earliest = input.now.getTime() + leadHours * 60 * 60 * 1000
  const latest = input.now.getTime() + horizonDays * 24 * 60 * 60 * 1000

  // Several published windows can land on one day — the panel writes one row
  // per opening, not one per day — so the day is the key and the openings
  // accumulate into it.
  const byDay = new Map<string, FreeInterval[]>()
  for (const window of input.windows) {
    if (!window.isBookable) continue
    if (window.endsAt.getTime() <= earliest) continue
    if (window.startsAt.getTime() >= latest) continue

    const day = zonedDay(window.startsAt)
    // A window that ends on the NEXT day ran to midnight. `zonedMinutes` reads
    // that back as 0, which would shut the room the instant it opened.
    const closesAt = zonedDay(window.endsAt) === day ? zonedMinutes(window.endsAt) : 24 * 60
    const opensAt = zonedMinutes(window.startsAt)
    if (closesAt <= opensAt) continue
    byDay.set(day, [...(byDay.get(day) ?? []), { from: opensAt, to: closesAt }])
  }

  const days: StudioDay[] = []
  for (const [date, openings] of [...byDay].sort(([a], [b]) => a.localeCompare(b))) {
    const midnight = zonedInstant(date, 0).getTime()
    /** An instant, as minutes past THIS day's midnight. DST-safe by subtraction. */
    const asMinutes = (at: Date) => (at.getTime() - midnight) / 60_000

    let free = openings
    // The lead time is a cut like any other, which is why it is applied here
    // rather than by skipping whole days: 24 hours from now lands in the middle
    // of a day, and that day keeps the hours after it.
    free = free.flatMap((span) =>
      without(span, { from: -Infinity, to: asMinutes(new Date(earliest)) }),
    )
    free = free.flatMap((span) =>
      without(span, { from: asMinutes(new Date(latest)), to: Infinity }),
    )
    for (const hold of input.holds) {
      const cut = { from: asMinutes(hold.blockedFrom), to: asMinutes(hold.blockedTo) }
      if (cut.to <= 0 || cut.from >= 24 * 60) continue
      free = free.flatMap((span) => without(span, cut))
    }

    if (free.length > 0) days.push({ date, free: free.sort((a, b) => a.from - b.from) })
  }
  return days
}

/** One weekday of the published week, as the note under the grid says it. */
export interface StudioWeekday {
  /** 0 = Sunday, matching `Date#getDay`. */
  readonly weekday: number
  readonly opensAt: number
  readonly closesAt: number
}

/**
 * The published week, for the line that says „Godziny otwarcia: …".
 *
 * Derived from the PUBLISHED windows and deliberately NOT from the free ones
 * {@link studioDays} returns: free intervals shrink as reservations land, so a
 * note built from those would announce that the studio opens at nine because
 * somebody happens to have booked next Monday morning.
 *
 * Where one weekday has been published with different hours on different dates,
 * this reports the widest — earliest open, latest close. The sentence is a
 * summary of a calendar that may be irregular, and the honest direction for a
 * summary to err is the one the calendar itself will then refuse, square by
 * square. The grid is the authority; this is prose beneath it.
 */
export function studioWeek(windows: readonly StudioWindow[]): StudioWeekday[] {
  const by = new Map<number, { opensAt: number; closesAt: number }>()
  for (const window of windows) {
    if (!window.isBookable) continue
    const day = zonedDay(window.startsAt)
    const closesAt = zonedDay(window.endsAt) === day ? zonedMinutes(window.endsAt) : 24 * 60
    const opensAt = zonedMinutes(window.startsAt)
    if (closesAt <= opensAt) continue
    // Read off the Warsaw DATE STRING, at midday UTC so no offset can move it,
    // rather than off either instant. `startsAt.getDay()` would be the weekday
    // where the process happens to run — on a UTC host, Warsaw's Monday 08:00
    // opening is Monday, but its midnight is Sunday — and `getUTCDay()` on the
    // instant has the same flaw for any window near either end of the day.
    const weekday = new Date(`${day}T12:00:00Z`).getUTCDay()
    const seen = by.get(weekday)
    by.set(weekday, {
      opensAt: Math.min(seen?.opensAt ?? opensAt, opensAt),
      closesAt: Math.max(seen?.closesAt ?? closesAt, closesAt),
    })
  }
  return [...by]
    .map(([weekday, hours]) => ({ weekday, ...hours }))
    .sort((a, b) => a.weekday - b.weekday)
}

/**
 * Every whole hour an `hours`-long block could start at on this day.
 *
 * On the hour, because spec §10 sells rooms that way and `slotProblem` refuses
 * anything else — a picker offering 10:30 would be offering a checkout error.
 */
export function studioStarts(day: StudioDay | undefined, hours: number): string[] {
  if (!day || hours <= 0) return []
  const starts: string[] = []
  for (const span of day.free) {
    const first = Math.ceil(span.from / 60) * 60
    for (let m = first; m + hours * 60 <= span.to; m += 60) {
      starts.push(`${String(Math.floor(m / 60)).padStart(2, '0')}:00`)
    }
  }
  return starts
}
